// The fields of a counterparty, as one component the register's form and the deal form's
// dialog both render (TX-US-0016). Of these only the name is required; every other field is on
// the form from the start, to be filled or left (TX-US-0037).
//
// Categories and commodities are sets, not choices. A trading house is a supplier and a
// customer; a supplier trades in more than one product. The four categories are cards, each saying
// what it means, and a picked Logistics or Service provider card offers its subcategories as pills
// (TX-US-0041); the
// commodities are a multi-select over the product list, and the country a combo box over the
// list of countries, both narrowed by typing (TX-US-0037).
//
// The engagement is the form's own panel (EngagementFields.jsx); the draft and the body here
// carry it.

import { COUNTRIES, countryLabel } from '../countries.js';
import Combobox from '../ui/Combobox.jsx';
import MultiSelect from '../ui/MultiSelect.jsx';
import { engagementBody, engagementDraft } from './engagement.js';
import { CATEGORIES, keptSubcategories, subcategoriesOf } from './labels.js';

// The address's limit, mirroring MAX_ADDRESS_LENGTH in trex/counterparties/models.py (TX-US-0042).
export const MAX_ADDRESS_LENGTH = 500;

export const DETAIL_FIELDS = [
  { name: 'legal_name', label: 'Registered name', placeholder: 'If it differs from the trading name' },
  { name: 'country', label: 'Country', placeholder: 'BH' },
  { name: 'city', label: 'City', placeholder: 'Ndola' },
  { name: 'region', label: 'Region', placeholder: 'Province, state or region' },
  { name: 'address', label: 'Address', placeholder: 'Street, building, PO box', multiline: true },
  { name: 'website', label: 'Website', placeholder: 'https://example.com', type: 'url' },
  { name: 'registration_number', label: 'Registration number', placeholder: 'CR-123456' },
  { name: 'tax_number', label: 'Tax number', placeholder: 'TRN-100200300' },
  { name: 'contact_name', label: 'Contact', placeholder: 'Who to ask for' },
  { name: 'contact_email', label: 'Contact email', placeholder: 'ops@example.com' },
  { name: 'contact_phone', label: 'Contact phone', placeholder: '+971 4 000 0000' },
  { name: 'notes', label: 'Notes', placeholder: 'Anything else worth knowing' },
];

// A draft with nothing in it, or the record's values ready to edit.
export const draftFrom = (record) => ({
  name: record?.name ?? '',
  ...Object.fromEntries(DETAIL_FIELDS.map((f) => [f.name, record?.[f.name] ?? ''])),
  categories: record?.categories ?? [],
  subcategories: record?.subcategories ?? [],
  commodities: record?.commodities ?? [],
  engagement: engagementDraft(record),
});

// What the API takes: blanks dropped, so an untouched field is absent rather than "".
export const bodyFrom = (draft) => ({
  name: draft.name.trim(),
  ...Object.fromEntries(
    DETAIL_FIELDS.map((f) => [f.name, draft[f.name]?.trim() || null]).filter(([, v]) => v !== null),
  ),
  categories: draft.categories,
  subcategories: keptSubcategories(draft.categories, draft.subcategories),
  commodities: draft.commodities,
  engagement: engagementBody(draft.engagement),
});

// The countries as options: the flag and the name to read, the name and the code to type. A
// stored value that is not on the list - free text from before the list, or an import - is shown
// as it is stored until another country is picked.
export const COUNTRY_OPTIONS = COUNTRIES.map(([code, name]) => ({
  value: code,
  label: countryLabel(code),
  text: name,
}));

const toggle = (list, code) =>
  list.includes(code) ? list.filter((c) => c !== code) : [...list, code];

export default function CounterpartyFields({ draft, onChange, products, nameSlot }) {
  const set = (field) => (value) => onChange((prev) => ({ ...prev, [field]: value }));

  return (
    <>
      <label className="field">
        <span className="field-label">Name</span>
        <input
          value={draft.name}
          onChange={(event) => set('name')(event.target.value)}
          placeholder="Vitol Bahrain B.S.C."
          autoFocus
          required
        />
      </label>
      {nameSlot}

      <fieldset className="category-cards">
        <legend className="field-label">Categories</legend>
        <p className="hint">What they are to Auremin. Pick one or more.</p>
        <div className="category-grid">
          {CATEGORIES.map((category) => {
            const ticked = draft.categories.includes(category.code);
            const subcategories = subcategoriesOf(category.code);
            const hintId = `category-hint-${category.code}`;
            return (
              <div key={category.code} className={`category-card${ticked ? ' selected' : ''}`}>
                {/* The checkbox is named by the category alone; the card's hint describes it. It is
                    invisible and covers the card, so a click anywhere on the card toggles it. */}
                <label className="category-card-title">
                  <input
                    type="checkbox"
                    aria-describedby={hintId}
                    checked={ticked}
                    onChange={() => onChange((prev) => {
                      const categories = toggle(prev.categories, category.code);
                      return { ...prev, categories, subcategories: keptSubcategories(categories, prev.subcategories) };
                    })}
                  />
                  <span className="category-card-check" aria-hidden="true">{ticked ? '✓' : ''}</span>
                  {category.label}
                </label>
                <p className="category-card-hint" id={hintId}>{category.hint}</p>
                {ticked && subcategories.length > 0 && (
                  <div className="subcategory-pills" role="group" aria-label={`${category.label} subcategories`}>
                    {subcategories.map((sub) => {
                      const on = draft.subcategories.includes(sub.code);
                      return (
                        <label key={sub.code} className={`pill${on ? ' on' : ''}`}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => set('subcategories')(toggle(draft.subcategories, sub.code))}
                          />
                          {sub.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </fieldset>

      {/* Not a <label>: the chips' buttons are inside the control, and a label would hand its
          clicks to the first of them. The input carries the name. */}
      <div className="field">
        <span className="field-label">Commodities</span>
        <MultiSelect
          label="Commodities"
          options={products.map((product) => ({ value: product.code, label: product.label }))}
          values={draft.commodities}
          onChange={set('commodities')}
          placeholder="Type to search the product list"
        />
      </div>

      {DETAIL_FIELDS.map((field) => (field.name === 'country'
        ? (
          <div className="field" key={field.name}>
            <span className="field-label">{field.label}</span>
            <Combobox label={field.label} options={COUNTRY_OPTIONS} value={draft.country ?? ''}
                      onChange={set('country')} placeholder="Type a country" />
          </div>
        )
        : (
          <label className="field" key={field.name}>
            <span className="field-label">{field.label}</span>
            {field.multiline ? (
              <textarea
                rows={2}
                maxLength={MAX_ADDRESS_LENGTH}
                value={draft[field.name] ?? ''}
                onChange={(event) => set(field.name)(event.target.value)}
                placeholder={field.placeholder}
              />
            ) : (
              <input
                type={field.type ?? 'text'}
                value={draft[field.name] ?? ''}
                onChange={(event) => set(field.name)(event.target.value)}
                placeholder={field.placeholder}
              />
            )}
          </label>
        )))}
    </>
  );
}
